import { api } from '../api.ts';
import { html, mount } from '../html.ts';
import { formatDate, isOverdue, PRIORITY, RISK_CATEGORY, t, TASK_STATUS } from '../i18n.ts';
import { state } from '../state.ts';
import { badge, emptyState, riskTone, statusBadge } from '../ui.ts';
import { renderSyncAlert } from '../components/sync-status.ts';
import { portalView } from './portal.ts';
import { pageHeader, projectLink, renderActivity, renderSignals } from './common.ts';

export async function dashboardView(container: HTMLElement): Promise<void> {
  // Guests (clients) get the portal instead of the team dashboard.
  if (state.me?.role === 'guest') return portalView(container);
  const data = await api.get('/dashboard');
  const c = data.counts;
  const firstName = state.me?.user.name.split(' ')[0] ?? '';
  mount(
    container,
    html`${pageHeader(t('Übersicht'), firstName ? t('Guten Tag, {name}.', { name: firstName }) : t('Guten Tag.'), t('Was heute Aufmerksamkeit braucht – über alle Projekte.'))}
    <div data-sync-alert hidden></div>
    <section class="p-metrics pma-metrics" data-p-columns="3" aria-label="${t('Kennzahlen')}">
      <a class="p-metric" href="/projects"><span>${t('Aktive Projekte')}</span><strong>${c.active_projects}</strong></a>
      <div class="p-metric"><span>${t('Offene Aufgaben')}</span><strong>${c.open_tasks}</strong></div>
      <div class="p-metric${c.overdue_tasks ? ' pma-metric--warning' : ''}"><span>${t('Überfällig')}</span><strong>${c.overdue_tasks}</strong></div>
      <div class="p-metric${c.critical_risks ? ' pma-metric--danger' : ''}"><span>${t('Kritische Risiken')}</span><strong>${c.critical_risks}</strong></div>
      <div class="p-metric"><span>${t('Gates in 14 Tagen')}</span><strong>${c.gates_due_soon}</strong></div>
      <div class="p-metric"><span>${t('Offene Entscheidungen')}</span><strong>${c.pending_decisions}</strong></div>
    </section>
    <section class="p-card" aria-labelledby="dash-signals">
      <div class="p-card__header"><h2 id="dash-signals">${t('Hinweise des Assistenten')}</h2><span class="pma-muted pma-small">${t('regelbasiert · mit Begründung')}</span></div>
      <div class="p-card__body" data-signals></div>
    </section>
    <div class="pma-grid-2">
      <section class="p-card" aria-labelledby="dash-tasks">
        <div class="p-card__header"><h2 id="dash-tasks">${t('Nächste Aufgaben')}</h2></div>
        <div class="p-card__body">${
          data.myTasks.length
            ? html`<ul class="pma-list">${data.myTasks.map(
                (task: any) => html`<li class="pma-list__row">
                  <div><a href="/projects/${task.project_id}?tab=tasks">${task.project_key}-${task.number}</a> ${task.title}
                    <div class="pma-muted pma-small">${task.project_name}</div></div>
                  <div class="pma-list__meta">${statusBadge(task.status, TASK_STATUS)} ${task.priority !== 'medium' ? statusBadge(task.priority, PRIORITY) : ''}
                    <span class="${isOverdue(task.due_on) ? 'pma-overdue' : 'pma-muted'}">${formatDate(task.due_on)}</span></div>
                </li>`,
              )}</ul>`
            : emptyState(t('Alles erledigt'), t('Keine offenen Aufgaben für dich.'))
        }</div>
      </section>
      <section class="p-card" aria-labelledby="dash-gates">
        <div class="p-card__header"><h2 id="dash-gates">${t('Anstehende Gates')}</h2></div>
        <div class="p-card__body">${
          data.upcomingGates.length
            ? html`<ul class="pma-list">${data.upcomingGates.map(
                (g: any) => html`<li class="pma-list__row">
                  <div><strong>${g.code}</strong> ${g.name}<div class="pma-muted pma-small">${projectLink(g, g.project_name)}</div></div>
                  <div class="pma-list__meta">
                    <span class="pma-small">${t('{met}/{total} Kriterien', { met: g.criteria_met, total: g.criteria_total })}</span>
                    <span class="${isOverdue(g.due_on) ? 'pma-overdue' : 'pma-muted'}">${formatDate(g.due_on)}</span></div>
                </li>`,
              )}</ul>`
            : emptyState(t('Keine offenen Gates'), t('Projekte mit Vorlage erzeugen Gates automatisch.'))
        }</div>
      </section>
      <section class="p-card" aria-labelledby="dash-risks">
        <div class="p-card__header"><h2 id="dash-risks">${t('Top-Risiken')}</h2></div>
        <div class="p-card__body">${
          data.topRisks.length
            ? html`<ul class="pma-list">${data.topRisks.map(
                (r: any) => html`<li class="pma-list__row">
                  <div>${r.title}<div class="pma-muted pma-small">${projectLink(r, r.project_name)} · ${RISK_CATEGORY[r.category]}</div></div>
                  <div class="pma-list__meta">${badge(t('Score {n}', { n: r.score }), riskTone(r.score))}</div>
                </li>`,
              )}</ul>`
            : emptyState(t('Keine offenen Risiken'), t('Erfasse Risiken im Projekt, um sie hier zu sehen.'))
        }</div>
      </section>
      <section class="p-card" aria-labelledby="dash-activity">
        <div class="p-card__header"><h2 id="dash-activity">${t('Letzte Aktivität')}</h2></div>
        <div class="p-card__body" data-activity></div>
      </section>
    </div>`,
  );
  await Promise.all([
    renderActivity(container.querySelector('[data-activity]')!, undefined, 12),
    renderSignals(container.querySelector('[data-signals]')!),
    renderSyncAlert(container.querySelector('[data-sync-alert]')!),
  ]);
}
